/**
 * Framing maths for the town map, kept apart from the renderer so it can be
 * exercised without a DOM.
 *
 * Deliberately standalone: `lib/geo.js` is the build-time twin of this file,
 * but only `public/` is copied into the deployed bundle, so the page cannot
 * import it. The two overlap in `ringsOf` alone, and both are tested.
 */

/**
 * The build normalises every stored boundary to Polygon or MultiPolygon
 * (`lib/geo.js`). The Feature arm stays because a snapshot cached before that
 * change can still be served to this page.
 */
export function ringsOf(geometry) {
  if (!geometry || typeof geometry !== 'object') return [];
  if (geometry.type === 'Polygon') return geometry.coordinates || [];
  if (geometry.type === 'MultiPolygon') return (geometry.coordinates || []).flat();
  if (geometry.type === 'Feature') return ringsOf(geometry.geometry);
  return [];
}

/**
 * Smallest span the frame may have, in degrees - about 450 m, a legible block
 * or two. A town with one listing, or every listing at one address, otherwise
 * gives a zero span, and the projection divides by it.
 */
export const MIN_SPAN_DEG = 0.004;

/** Fraction of the span left as breathing room on each side. */
export const PAD_FRACTION = 0.06;

function extent(values) {
  return { min: Math.min(...values), max: Math.max(...values) };
}

/**
 * The geographic window the map draws.
 *
 * The frame follows the listings, not the boundary: a stored boundary often
 * runs far out over the water, and framing on it would squeeze the built-up
 * part of town into a corner.
 *
 * That only works while the listings are actually in the town. A single stray
 * coordinate - null island, or a listing attached to the wrong municipality -
 * used to stretch the frame across tens of degrees, leaving the outline at a
 * fraction of a percent of the canvas: the reported "map has no graphic".
 * The build now withholds those points, and the frame is clamped to the
 * boundary here as well, so a stale snapshot cannot bring the collapse back.
 */
export function computeFrame(points, rings) {
  const hasPoints = points.length > 0;
  const coords = rings.flat();

  if (!hasPoints && !coords.length) return null;

  let lng = hasPoints
    ? extent(points.map(p => p.lng))
    : extent(coords.map(c => c[0]));
  let lat = hasPoints
    ? extent(points.map(p => p.lat))
    : extent(coords.map(c => c[1]));

  if (hasPoints && coords.length) {
    const bLng = extent(coords.map(c => c[0]));
    const bLat = extent(coords.map(c => c[1]));
    // Never frame wider than the town itself: the outline is the one thing on
    // this canvas guaranteed to be in the right place.
    lng = { min: Math.max(lng.min, bLng.min), max: Math.min(lng.max, bLng.max) };
    lat = { min: Math.max(lat.min, bLat.min), max: Math.min(lat.max, bLat.max) };

    // Clamping can invert the window if every point sat outside the outline.
    // Fall back to the outline, which is always drawable.
    if (lng.min > lng.max) lng = bLng;
    if (lat.min > lat.max) lat = bLat;
  }

  lng = widenTo(lng, MIN_SPAN_DEG);
  lat = widenTo(lat, MIN_SPAN_DEG);

  const lngPad = (lng.max - lng.min) * PAD_FRACTION;
  const latPad = (lat.max - lat.min) * PAD_FRACTION;

  return {
    minLng: lng.min - lngPad,
    maxLng: lng.max + lngPad,
    minLat: lat.min - latPad,
    maxLat: lat.max + latPad
  };
}

function widenTo(range, minSpan) {
  if (range.max - range.min >= minSpan) return range;
  const mid = (range.max + range.min) / 2;
  return { min: mid - minSpan / 2, max: mid + minSpan / 2 };
}

// ---------------------------------------------------------------------------
// Web Mercator
//
// The page used to project equirectangularly, which needs no tile server and
// is accurate enough on its own. A satellite backdrop changes the requirement:
// the overlay must use the *same* projection as the imagery or markers drift
// against the roads beneath them.
//
// The drift is modest - about 2 m across Old Orchard Beach's 0.09 degrees of
// latitude, under one marker radius, and about 25 m across Austin's 0.42,
// which is half a block. Matching the tile scheme exactly costs nothing and
// removes the question entirely, which is the better trade either way.
// ---------------------------------------------------------------------------

/** Pixels per world at zoom 0 - the constant every tile scheme is built on. */
export const TILE_SIZE = 256;

/** Longitude to a 0..1 fraction of the world, west to east. */
export function mercatorX(lng) {
  return (lng + 180) / 360;
}

/** Latitude to a 0..1 fraction of the world, north to south. */
export function mercatorY(lat) {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const phi = (clamped * Math.PI) / 180;
  return (1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2;
}

/** The inverse of `mercatorY`, for finding the centre latitude of a frame. */
export function inverseMercatorY(y) {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI;
}

/**
 * Chooses the centre, zoom and pixel size of a basemap image that contains
 * `frame`, and returns a projector onto that image.
 *
 * Zoom is an integer because that is all the Static Maps API accepts, and it
 * is rounded *down* so the image always covers at least the frame - rounding
 * up would crop listings out of view. The leftover is context, which is what
 * the surrounding streets are for.
 */
export function fitView(frame, { maxPx = 640, minPx = 320 } = {}) {
  if (!frame) return null;

  const x0 = mercatorX(frame.minLng);
  const x1 = mercatorX(frame.maxLng);
  const y0 = mercatorY(frame.maxLat); // north edge: smaller y
  const y1 = mercatorY(frame.minLat);

  const dx = Math.max(x1 - x0, Number.EPSILON);
  const dy = Math.max(y1 - y0, Number.EPSILON);

  // The image takes the frame's own aspect, so neither axis is stretched.
  const width = maxPx;
  const height = Math.round(Math.max(minPx, Math.min(maxPx, (maxPx * dy) / dx)));

  const zoom = Math.max(
    0,
    Math.min(
      21,
      Math.floor(Math.min(Math.log2(width / (TILE_SIZE * dx)), Math.log2(height / (TILE_SIZE * dy))))
    )
  );

  const centerLng = (frame.minLng + frame.maxLng) / 2;
  const centerLat = inverseMercatorY((y0 + y1) / 2);

  const worldPx = TILE_SIZE * 2 ** zoom;
  const cx = mercatorX(centerLng);
  const cy = mercatorY(centerLat);

  return {
    zoom,
    centerLat,
    centerLng,
    width,
    height,
    /** Longitude to an x in image pixels. */
    px: lng => (mercatorX(lng) - cx) * worldPx + width / 2,
    /** Latitude to a y in image pixels; north is up. */
    py: lat => (mercatorY(lat) - cy) * worldPx + height / 2
  };
}
